import { ref, computed, defineComponent, watchEffect } from 'vue';
import type { PropType } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import { CloseIcon } from 'tdesign-icons-vue-next';

import TLoading from '../loading';
import { useTNodeJSX } from '../hooks/tnode';
import { usePrefixClass } from '../hooks/useClass';

import props from './props';

export default defineComponent({
  name: 'TImage',
  props,
  setup(props, context) {
    const imageClass = usePrefixClass('image');
    const renderTNodeJSX = useTNodeJSX();

    // 默认loading和error状态展示，slot支持Node和Function
    const closeIcon = <CloseIcon size="22px" />;
    const LoadingIcon = <TLoading theme="dots" inheritColor={true} />;

    // 记录图片的loading、error状态
    const isLoading = ref(true);
    const isError = ref(false);

    // 图片自定义样式
    const imageStyles = computed(() => ({
      objectFit: props.fit,
      objectPosition: props.position,
    }));
    const imageClasses = computed(() => ({
      [`${imageClass.value}`]: true,
      [`${imageClass.value}--${props.shape}`]: true,
    }));

    // 图片懒加载
    const imageDOM = ref();
    const realSrc = ref('');

    watchEffect(() => {
      realSrc.value = props.lazy ? '' : props.src;
    });

    const { stop } = useIntersectionObserver(imageDOM, ([{ isIntersecting }], observerElement) => {
      if (isIntersecting && props.lazy) {
        // 停止监听
        stop();
        realSrc.value = props.src;
      }
    });

    // 图片加载完成回调
    const handleImgLoadCompleted = (e: Event) => {
      props.onLoad?.({ e });
      isLoading.value = false;
    };

    // 图片加载失败回调
    const handleImgLoadError = (e: Event) => {
      if (realSrc.value === '') {
        return;
      }
      props.onError?.({ e });
      isLoading.value = false;
      isError.value = true;
      if (props.fallback) {
        realSrc.value = props.fallback;
        isError.value = false;
      }
    };

    const maskContent = computed(() => {
      const statusContent = () => {
        if (context.slots?.loading && isLoading.value) {
          return renderTNodeJSX('loading');
        }
        if (!context.slots?.loading && isLoading.value) {
          return LoadingIcon;
        }
        if (context.slots?.error && isError.value) {
          return renderTNodeJSX('error');
        }
        if (!context.slots?.error && isError.value) {
          return closeIcon;
        }
        return false;
      };
      const node = () => {
        if (isLoading.value || isError.value) {
          if (props.lazy && realSrc.value === '') {
            return;
          }
          return <div class={`${imageClass.value}__mask`}>{statusContent()}</div>;
        }
      };
      return node();
    });

    return () => {
      return (
        <div class={imageClasses.value}>
          {maskContent.value}
          <picture>
            {props.srcset &&
              Object.entries(props.srcset).map((item, index) => <source key={index} type={item[0]} srcset={item[1]} />)}
            <img
              ref={imageDOM}
              class={`${imageClass.value}__img`}
              style={imageStyles.value}
              src={realSrc.value}
              alt={props.alt}
              referrerpolicy={props.referrerpolicy}
              onLoad={handleImgLoadCompleted}
              onError={handleImgLoadError}
            />
          </picture>
        </div>
      );
    };
  },
});
